import tw from 'twin.macro';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleNarrowPage from '@teetsh/app/src/components/Layouts/SimpleNarrowPage';
import { NewOptionCard } from '@teetsh/app/src/components/Cards/NewOptionCard';
import { useSchoolGoTo } from '@teetsh/app/src/containers/App/useSchoolGoTo';
import {
  EDTFromScratchIllustration,
  EDTFromExistingIllustration,
  EDTFromTemplateIllustration,
} from '../EDTIllustrations';
import useCreateEDT from '@teetsh/app/src/queries/edt/useCreateEDT';
import { useAllEDTs } from 'queries/edt/useAllEDTs';
import { useUser } from 'containers/App/context/useUser';
import { OnboardingModulePageWrapper } from '@teetsh/app/src/components/OnboardingModulePageWrapper';
import useEDTTemplates from './useEDTTemplates';

export function NewEDTPage() {
  const goTo = useSchoolGoTo();
  const createEDT = useCreateEDT();
  const { scholarSystem } = useUser();
  const { t } = useTranslation(['emploiDuTemps']);
  const { data: oldEdts, isLoading: isLoadingOldEdts } = useAllEDTs();
  const { data: templates, isLoading: isLoadingTemplates } = useEDTTemplates({
    scholarSystem,
  });
  const hasOldEdts = (oldEdts || []).length > 0;
  const hasTemplates = (templates || []).length > 0;
  const onCreate = () =>
    createEDT(undefined).then(edt => goTo(`emploi-du-temps/${edt.id}`));

  // When only one option available we automatically create and redirect
  useEffect(() => {
    if (!isLoadingOldEdts && !isLoadingTemplates && !hasOldEdts && !hasTemplates) {
      onCreate();
    }
  }, [isLoadingOldEdts, isLoadingTemplates, hasOldEdts, hasTemplates]);

  if (isLoadingOldEdts || isLoadingTemplates) {
    return (
      <OnboardingModulePageWrapper
        title={t('emploiDuTemps:new.title')}
        onBack={() => goTo('emploi-du-temps')}
      >
        <SimpleNarrowPage width="5xl">
          <div tw="mx-auto max-w-5xl grid items-center justify-center md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="tsh-skeleton" tw="aspect-square" />
            <div className="tsh-skeleton" tw="aspect-square" />
            <div className="tsh-skeleton" tw="aspect-square" />
          </div>
        </SimpleNarrowPage>
      </OnboardingModulePageWrapper>
    );
  }

  return (
    <OnboardingModulePageWrapper
      title={t('emploiDuTemps:new.title')}
      onBack={() => goTo('emploi-du-temps')}
    >
      <SimpleNarrowPage width="5xl">
        <div
          css={[
            tw`mb-6 text-center text-balance grid justify-center gap-4 grid-cols-1 auto-rows-fr`,
            hasOldEdts && hasTemplates
              ? tw`lg:grid-cols-3`
              : tw`lg:grid-cols-2`,
          ]}
        >
          <NewOptionCard
            title={t('emploiDuTemps:new.cards.fromScratch.title')}
            description={t('emploiDuTemps:new.cards.fromScratch.description')}
            icon={<EDTFromScratchIllustration />}
            onClick={onCreate}
          />
          {hasOldEdts && (
            <NewOptionCard
              title={t('emploiDuTemps:new.cards.copyExisting.title')}
              description={t(
                'emploiDuTemps:new.cards.copyExisting.description',
              )}
              icon={<EDTFromExistingIllustration />}
              onClick={() => goTo('emploi-du-temps/new/from-old')}
            />
          )}
          {hasTemplates && (
            <NewOptionCard
              title={t('emploiDuTemps:new.cards.useTemplate.title')}
              description={t('emploiDuTemps:new.cards.useTemplate.description')}
              icon={<EDTFromTemplateIllustration />}
              onClick={() => goTo('emploi-du-temps/new/with-template')}
            />
          )}
        </div>
      </SimpleNarrowPage>
    </OnboardingModulePageWrapper>
  );
}
